/** Milliseconds between type-ahead keystrokes before the search starts over. */
export const TYPEAHEAD_TIMEOUT = 500;

export type MarkListItem = {
  id: string;
  label: string;
  details: readonly string[];
};

export type MarkExpansion = 'expanded' | 'collapsed' | 'leaf';

export type MarkTreeItem = MarkListItem & {
  parentId: string | null;
  level: number;
  setSize: number;
  posInSet: number;
  expansion: MarkExpansion;
};

export type Typeahead = { text: string; at: number };

export type MarkListNavigation =
  | { type: 'step'; step: 1 | -1 }
  | { type: 'first' }
  | { type: 'last' }
  | { type: 'typeahead'; character: string; at: number };

export type MarkListState = {
  activeId: string | null;
  typeahead: Typeahead | null;
};

export type MarkTreeCommand =
  | { type: 'move'; id: string }
  | { type: 'expand'; id: string }
  | { type: 'collapse'; id: string };

export type MarkKeyCommand =
  | { type: 'navigate'; navigation: MarkListNavigation }
  | { type: 'activate' }
  | { type: 'clear' }
  | { type: 'contextMenu' }
  | { type: 'tree'; key: 'ArrowLeft' | 'ArrowRight' };

export type MarkKeyEvent = {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  timeStamp: number;
};

const isPrintableCharacter = (key: string): boolean =>
  key.length === 1 && key !== ' ';

const isTypeaheadRunning = (
  typeahead: Typeahead | null,
  at: number
): typeahead is Typeahead =>
  typeahead !== null && at - typeahead.at <= TYPEAHEAD_TIMEOUT;

/** The command for a key, given the type-ahead in progress: during a search, Space is typed. */
export const toMarkKeyCommand = (
  { key, shiftKey, ctrlKey, metaKey, altKey, timeStamp }: MarkKeyEvent,
  typeahead: Typeahead | null
): MarkKeyCommand | null => {
  if (key === 'ContextMenu' || (key === 'F10' && shiftKey)) {
    return { type: 'contextMenu' };
  }
  if (ctrlKey || metaKey || altKey) return null;
  if (key === ' ' && isTypeaheadRunning(typeahead, timeStamp)) {
    return {
      type: 'navigate',
      navigation: { type: 'typeahead', character: key, at: timeStamp },
    };
  }

  switch (key) {
    case 'ArrowDown':
      return { type: 'navigate', navigation: { type: 'step', step: 1 } };
    case 'ArrowUp':
      return { type: 'navigate', navigation: { type: 'step', step: -1 } };
    case 'Home':
      return { type: 'navigate', navigation: { type: 'first' } };
    case 'End':
      return { type: 'navigate', navigation: { type: 'last' } };
    case 'Enter':
    case ' ':
      return { type: 'activate' };
    case 'Escape':
      return { type: 'clear' };
    case 'ArrowLeft':
    case 'ArrowRight':
      return { type: 'tree', key };
  }

  if (!isPrintableCharacter(key)) return null;
  return {
    type: 'navigate',
    navigation: { type: 'typeahead', character: key, at: timeStamp },
  };
};

const clampIndex = (index: number, length: number): number =>
  Math.min(Math.max(index, 0), length - 1);

const toTypeaheadText = (
  previous: Typeahead | null,
  character: string,
  at: number
): string => {
  const prefix = isTypeaheadRunning(previous, at) ? previous.text : '';
  return `${prefix}${character.toLocaleLowerCase()}`;
};

const isRepeatedCharacter = (text: string): boolean =>
  [...text].every(character => character === text[0]);

const toRotated = <T>(items: readonly T[], start: number): T[] => [
  ...items.slice(start),
  ...items.slice(0, start),
];

const findTypeaheadMatch = (
  items: readonly MarkListItem[],
  activeIndex: number,
  text: string
): MarkListItem | undefined => {
  const isCycling = isRepeatedCharacter(text);
  const prefix = isCycling ? text[0] : text;
  const start = isCycling ? activeIndex + 1 : Math.max(activeIndex, 0);
  return toRotated(items, clampIndex(start % items.length, items.length)).find(
    ({ label }) => label.toLocaleLowerCase().startsWith(prefix)
  );
};

export const toActiveIndex = (
  items: readonly MarkListItem[],
  activeId: string | null
): number =>
  activeId === null ? -1 : items.findIndex(({ id }) => id === activeId);

/**
 * The row a navigation key moves to. Arrows step and stop at either end rather than wrapping; a
 * first arrow from no active row lands on the first one. Type-ahead matches the start of a label,
 * and repeating one character cycles through the labels starting with it.
 */
export const reduceMarkList = (
  state: MarkListState,
  items: readonly MarkListItem[],
  navigation: MarkListNavigation
): MarkListState => {
  if (items.length === 0) return { activeId: null, typeahead: null };
  const activeIndex = toActiveIndex(items, state.activeId);

  switch (navigation.type) {
    case 'step': {
      const index =
        activeIndex === -1
          ? 0
          : clampIndex(activeIndex + navigation.step, items.length);
      return { activeId: items[index].id, typeahead: null };
    }
    case 'first':
      return { activeId: items[0].id, typeahead: null };
    case 'last':
      return { activeId: items[items.length - 1].id, typeahead: null };
    case 'typeahead': {
      const text = toTypeaheadText(
        state.typeahead,
        navigation.character,
        navigation.at
      );
      const match = findTypeaheadMatch(items, activeIndex, text);
      return {
        activeId: match?.id ?? state.activeId,
        typeahead: { text, at: navigation.at },
      };
    }
  }
};

/**
 * What Left or Right does on a tree row: Right opens a closed row, then moves into an open one;
 * Left closes an open row, then moves to its parent.
 */
export const toTreeCommand = (
  items: readonly MarkTreeItem[],
  activeId: string | null,
  key: 'ArrowLeft' | 'ArrowRight'
): MarkTreeCommand | null => {
  const index = items.findIndex(({ id }) => id === activeId);
  const item = items[index];
  if (!item) return null;

  if (key === 'ArrowRight') {
    if (item.expansion === 'collapsed') return { type: 'expand', id: item.id };
    const child = items[index + 1];
    if (item.expansion === 'expanded' && child?.parentId === item.id) {
      return { type: 'move', id: child.id };
    }
    return null;
  }

  if (item.expansion === 'expanded') return { type: 'collapse', id: item.id };
  const isParentListed = items.some(({ id }) => id === item.parentId);
  if (item.parentId === null || !isParentListed) return null;
  return { type: 'move', id: item.parentId };
};

export type HighlightedMarkArgs = {
  activeId: string | null;
  ids: ReadonlySet<string>;
  isFocusWithin: boolean;
};

/** The listed mark the canvas should highlight: the active row, while focus is in the chart. */
export const toHighlightedMarkId = ({
  activeId,
  ids,
  isFocusWithin,
}: HighlightedMarkArgs): string | null => {
  if (!isFocusWithin || activeId === null) return null;
  return ids.has(activeId) ? activeId : null;
};
